import { createFileRoute, redirect } from '@tanstack/react-router';
import { lazy, Suspense } from 'react';

import { queryClient } from '~/api/queryClient';
import { queryKeys } from '~/api/queryKeys';
import { PageSkeleton } from '~/components/common/page-skeleton/page-skeleton';
import { fetchExperimentPipelineStatus } from '~/features/content-enrichment/services/content-scoring.service';

const EnrichmentOverviewPage = lazy(
  () => import('~/features/content-enrichment/pages/enrichment-overview-page'),
);

// Backend stage name the pipeline-status response uses for the post-enrichment
// re-scoring stage. Mirrors the constant in `use-full-catalog-progress.ts`;
// duplicated here so the route-level guard doesn't pull in a hook module.
const POST_SCORING_STAGE = 'PostScoring';

/**
 * Route gate: the Enrichment Overview page reads the catalog quality score
 * (`afterScore`) populated at the end of the PostScoring stage. Landing here
 * before that stage stamps `completed` renders `null` scores as `0/100` with a
 * `-36.83` delta in red — which reads as "enrichment made things worse" when
 * really the number just hasn't been computed yet.
 *
 * Mirrors the auto-advance condition in `full-catalog-page.tsx`: both
 * Enrichment AND PostScoring must be `completed` before the user can land on
 * Overview. Until then, bounce them back to Full Catalog Generation so they
 * see live stage progress on the timeline (PR #213 wired live counts there)
 * instead of a misleading zeroed result page.
 *
 * On any read error (404, network), allow the navigation — the page already
 * has its own loading + error states, and a guard failure shouldn't deadlock
 * the user out of a screen they got to from a valid backend state earlier.
 */
export const Route = createFileRoute(
  '/_authenticated/_tenant/experiments_/$expId/content-enrichment/overview',
)({
  beforeLoad: async ({ params }) => {
    let status;
    try {
      status = await queryClient.ensureQueryData({
        queryKey: queryKeys.experiments.pipelineStatus(params.expId),
        queryFn: () => fetchExperimentPipelineStatus(params.expId),
      });
    } catch {
      // Fail-open: if the status fetch errors (404 on a missing experiment is
      // already handled by the page-level guard), let the page render its own
      // empty/error state rather than trapping the user in a redirect loop.
      return;
    }

    // FAILED / REJECTED runs route to /full where the FailedStateCard surfaces
    // the failure reason + retry CTA. Landing on /overview after a failure
    // would render zeroed scores with no context for what went wrong.
    if (status.status === 'FAILED' || status.status === 'REJECTED') {
      // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack Router uses throw redirect() as control flow
      throw redirect({
        to: '/experiments/$expId/content-enrichment/full',
        params: { expId: params.expId },
      });
    }

    const postScoringDone = (status.stages ?? []).some(
      (s) => s.name === POST_SCORING_STAGE && s.status === 'completed',
    );
    if (!postScoringDone) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack Router uses throw redirect() as control flow
      throw redirect({
        to: '/experiments/$expId/content-enrichment/full',
        params: { expId: params.expId },
      });
    }
  },
  component: () => (
    <Suspense fallback={<PageSkeleton />}>
      <EnrichmentOverviewPage />
    </Suspense>
  ),
});
